/**
 * DOM surgery for one paragraph: turns a character-range change into Word
 * tracked-change markup.
 *
 * Rules the whole class obeys, each with the case that taught it:
 *   - Text never moves between runs. Formatting lives on the run, so the text
 *     that stays keeps its run and the text that goes is split into its own
 *     run first (`isolate`), then wrapped or vacated in place. Moving text
 *     between runs once turned a bold defined term plain.
 *   - New text copies the formatting of the run it lands next to (`shellOf`),
 *     minus any `rPrChange`, so we never repeat someone's formatting revision.
 *     Copying the record made every insertion show a phantom "formatted" change.
 *   - Our own insertions are vacated, not struck, under the "vacate" policy a
 *     write into the document runs with: deleting text we inserted removes it,
 *     exactly as Word does when an author deletes their own insertion. An edit
 *     or a revert drawn for a card's preview runs with the "strike" policy and
 *     strikes them like anyone else's, so the card shows the words it takes
 *     back for review (`OwnInsertionEditPolicy`, `OwnChangeWriting`).
 *   - A `w:ins` never nests inside another `w:ins`. When an insertion lands
 *     inside a foreign insertion the container is split around it
 *     (`placeInsertion`). Word rejects the nested form on Accept.
 *   - Structural containers (content controls, hyperlinks, fields) keep their
 *     text. Replacement text goes inside the same container as the text it
 *     replaces, shared out proportionally when a deletion spans several
 *     containers; a content control left with no text is dropped by Word, and
 *     with it the control's binding.
 *   - Everything is done in accepted-text offsets planned before the first
 *     edit; the engine applies edits back to front so each splice leaves every
 *     earlier edit's offsets untouched.
 */
import {
    DELETION_CONTAINERS,
    fieldResultIsRedrawnByWord,
    GLYPH_ELEMENTS,
    INSERTION_CONTAINERS,
    isGlyphChar,
    elementNameWithoutPrefix,
    REVISION_CONTAINERS,
    STRUCTURAL_HOMES,
    WORDPROCESSINGML_NS,
    XML_NS,
    DOCUMENT_POSITION_FOLLOWING,
} from "./ooxml/ooxml-names";
import { LinkFieldStriker, type ReplacementLinkOfStruckLink } from "./ooxml/link-field-striker";
import { OoxmlBookmarks } from "./ooxml/ooxml-bookmarks";
import type { ParagraphModel, TextPiece } from "./ooxml/paragraph-model";
import { RunSpelling } from "./ooxml/run-spelling";
import type { Revision } from "./ooxml/revision";
import { type ComplexField, complexFieldWalkOf } from "./ooxml/word-owned-text-unlinker";

/**
 * What an edit does to text this author inserted earlier.
 *
 * CASES
 *   - "vacate" -> the text is removed outright, as Word removes an author's own insertion: an edit or a revert
 *                 written into the document
 *   - "strike" -> the text is struck by this author inside the insertion, as another author's would be: an edit
 *                 or a revert drawn for a card's preview, so the card shows the words it takes back
 */
export type OwnInsertionEditPolicy = "vacate" | "strike";

/** A character-range edit in accepted-text coordinates: replace `startOffset..endOffset` with `replacementText` (empty for a pure deletion). */
export interface PlannedEdit {
    startOffset: number;
    endOffset: number;
    replacementText: string;
}

/**
 * One run struck or vacated while deleting a span. `placementAnchor` is the
 * element replacement text is placed after; `formattingSourceRun` is the run
 * whose properties that text copies; `vacatedOwnInsertion` says the run was
 * our own insertion and was emptied rather than struck.
 */
interface StruckRun {
    placementAnchor: Element;
    formattingSourceRun: Element | null;
    deletedCharacterCount: number;
    vacatedOwnInsertion: boolean;
}

/**
 * The structural container a deletion sat in (a content control, a hyperlink,
 * or the paragraph itself) with the struck runs it holds merged into one.
 * Replacement text is shared out across homes so every container that lost
 * text gets some of the new text back: a deletion that crossed out of a
 * content control and put all the new words outside it left the control empty,
 * and Word deletes an empty control.
 */
interface DeletionHome {
    placementAnchor: Element;
    formattingSourceRun: Element | null;
    deletedCharacterCount: number;
}

/** What an insertion is built from (`insertionOf`): the run beside the words, the document, the words, and the link they are written as. */
interface InsertionOfWords {
    /**
     * The run beside the words, whose formatting plain words take.
     *
     * NULL
     *   No run supplies formatting: the words start from a bare run.
     */
    formattingSourceRun: Element | null;
    doc: Document;
    text: string;
    /**
     * The new link the words are written inside (a card's "change").
     *
     * NULL
     *   Plain words.
     */
    writtenAsLink: ReplacementLinkOfStruckLink | null;
}

/** A slice of a piece we deleted earlier that the new request wants back, in offsets local to that piece's text. */
export interface RestorableRange {
    piece: TextPiece;
    fromOffsetInPiece: number;
    toOffsetInPiece: number;
}

/** The struck spelling of a run's text and instruction children, and the live spelling each comes back as. */
const LIVE_SPELLING_OF_STRUCK_CHILD: Record<string, string> = { delText: "t", delInstrText: "instrText" };
/** The run child that anchors a Word comment's reference mark; a run holding one is never copied, or the comment would be anchored twice. */
const COMMENT_REFERENCE_TAG = "commentReference";

export class ParagraphSplicer {
    constructor(
        private readonly paragraphModel: ParagraphModel,
        private readonly appliedRevision: Revision,
        private readonly ownInsertionEditPolicy: OwnInsertionEditPolicy,
    ) {}

    /**
     * Strikes `startOffset..endOffset`, then places `replacementText` after the
     * struck text, one insertion per structural home. A pure insertion (empty
     * range) goes into the run the offset falls in, or after the last run when
     * appending. Callers apply edits back to front so earlier offsets stay
     * valid.
     *
     * CASES, by `writtenAsLink`
     *   - a link   -> each insertion's words are written inside a new HYPERLINK field to its
     *                 address, formatted as the struck link's text (a card's "change")
     *   - null     -> plain words, formatted as the run beside them
     */
    applyEdit(edit: PlannedEdit, writtenAsLink: ReplacementLinkOfStruckLink | null): void {
        const { paragraphModel: model, appliedRevision: revision } = this;
        const paragraphElement = model.paragraph;

        /**
         * SCOPE
         *   Local to applyEdit.
         *   Covers only the deletion half of one replacement edit.
         *
         * CONSEQUENCE
         *   - strike or vacate exactly the accepted pieces overlapped by this edit
         *   - return placement anchors for any replacement text this same edit inserts
         */
        const strikeOrVacateDeletionSpan = (): StruckRun[] => {
            // Deleting our own insertion is a plain removal, as in Word, under the
            // "vacate" policy and unless another author's revision wraps the run.
            // Inside a foreign revision the text must stay visible as struck so their
            // change stays reviewable; under "strike" every own insertion is struck.
            const collapsesIntoOwnInsertion = (piece: TextPiece, run: Element): boolean => {
                if (this.ownInsertionEditPolicy === "strike" || !revision.isBy(piece.insertedBy)) return false;
                for (let ancestorElement = run.parentElement; ancestorElement && ancestorElement !== paragraphElement; ancestorElement = ancestorElement.parentElement) {
                    /**
                     * WHEN an enclosing revision container belongs to another author
                     *   CONSEQUENCE
                     *     This run does not collapse as our own insertion.
                     *     It must stay visible as a struck deletion so that foreign revision
                     *     markup remains reviewable.
                     */
                    if (REVISION_CONTAINERS.has(elementNameWithoutPrefix(ancestorElement)) && !revision.isBy(ancestorElement.getAttributeNS(WORDPROCESSINGML_NS, "author"))) return false;
                }
                return true;
            };
            const removeNonPropertyChildrenFromOwnInsertionRun = (run: Element, piece: TextPiece): Element => {
                /**
                 * WHEN the run being vacated holds the piece's own text node
                 *   CONSEQUENCE
                 *     the piece's characters are the ones going, so the piece leaves the accepted text.
                 * WHEN it holds a part split off the piece instead
                 *   CONSEQUENCE
                 *     the piece's node keeps the characters before the cut, which a later edit
                 *     (applied back to front, so at a smaller offset) may still strike or
                 *     vacate; marking the piece deleted here hid them, and the later edit then
                 *     found no live piece, so "one (1)" stayed and "three (3)" was appended
                 *     after "days" instead of replacing it.
                 */
                const runHoldsThePieceText = ParagraphSplicer.holds(run, piece.textNode);
                for (const runChildElement of Array.from(run.children)) {
                    /**
                     * CASES
                     *   - child is rPr      -> keep it as the formatting shell
                     *   - child is anything else
                     *       -> remove it while vacating our own insertion
                     *
                     * EXAMPLE
                     *   children = [w:rPr, w:t, w:proofErr]
                     *   kept     = [w:rPr]
                     *   removed  = [w:t, w:proofErr]
                     */
                    if (elementNameWithoutPrefix(runChildElement) !== "rPr") run.removeChild(runChildElement);
                }
                if (runHoldsThePieceText) piece.markDeletedBy(revision.author);
                return run;
            };
            const toDeletedText = (run: Element, piece: TextPiece): void => {
                const ownerDocument = run.ownerDocument as Document;
                /**
                 * WHEN the run is an equation's run
                 *   CONSEQUENCE
                 *     leave its `m:t` alone: math has no struck spelling of its text element,
                 *     and Word marks a deleted math run by the `w:del` inside it instead.
                 */
                if (RunSpelling.isMath(run)) return;
                for (const runChildElement of Array.from(run.children)) {
                    /**
                     * CASES
                     *   - child is w:t      -> convert it to w:delText
                     *   - child is not w:t  -> leave it unchanged
                     */
                    if (elementNameWithoutPrefix(runChildElement) !== "t") continue;
                    const replacementNode = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:delText");
                    ParagraphSplicer.setText(replacementNode, runChildElement.textContent ?? "");
                    run.replaceChild(replacementNode, runChildElement);
                    /**
                     * WHEN the replaced node is this piece's textNode
                     *   CONSEQUENCE
                     *     Update the piece to point at w:delText so later local offsets still
                     *     refer to the same characters.
                     */
                    if (runChildElement === piece.textNode) piece.replaceTextNode(replacementNode);
                }
            };
            const wrapInDeletion = (run: Element): Element => {
                const deletionWrapper = revision.stamp((run.ownerDocument as Document).createElementNS(WORDPROCESSINGML_NS, "w:del"));
                /**
                 * WHEN the run is an equation's run
                 *   CONSEQUENCE
                 *     the deletion goes inside it, over the character formatting and the text,
                 *     which is how Word writes a struck equation run; the math properties stay
                 *     on the run itself, where the equation renderer reads them. The run is the
                 *     anchor replacement text is placed after, so the new text lands in the same
                 *     math argument the struck text sat in.
                 */
                if (RunSpelling.isMath(run)) {
                    for (const runChildElement of Array.from(run.children)) {
                        const childIsMathRunProperties = elementNameWithoutPrefix(runChildElement) === "rPr" && RunSpelling.isMath(runChildElement);
                        if (childIsMathRunProperties) continue;
                        deletionWrapper.appendChild(runChildElement);
                    }
                    run.appendChild(deletionWrapper);
                    return run;
                }
                run.parentNode?.insertBefore(deletionWrapper, run);
                deletionWrapper.appendChild(run);
                return deletionWrapper;
            };

            /**
             * CASES
             *   - piece.startOffset < edit.endOffset
             *     and piece.endOffset > edit.startOffset
             *       -> piece has non-empty overlap with the deletion span
             *   - piece only touches at one boundary
             *       -> exclude it
             */
            const acceptedPiecesOverlappingDeletionRange = model.acceptedPieces().filter((piece) => piece.startOffset < edit.endOffset && piece.endOffset > edit.startOffset);
            const struckRuns: StruckRun[] = [];
            for (const overlappingAcceptedPiece of acceptedPiecesOverlappingDeletionRange) {
                /**
                 * EXAMPLE
                 *   edit.startOffset                  = 10
                 *   overlappingAcceptedPiece.startOffset = 8
                 *   max(10, 8)                        -> 10
                 *   10 - 8                            -> 2
                 *   overlap starts 2 characters into this piece
                 */
                const overlapStartOffsetInPiece = Math.max(edit.startOffset, overlappingAcceptedPiece.startOffset) - overlappingAcceptedPiece.startOffset;
                /**
                 * EXAMPLE
                 *   edit.endOffset                    = 14
                 *   overlappingAcceptedPiece.startOffset = 8
                 *   overlappingAcceptedPiece.endOffset   = 16
                 *   min(14, 16)                        -> 14
                 *   14 - 8                             -> 6
                 *
                 *   With overlapStartOffsetInPiece = 2:
                 *     deleted slice -> this piece's characters 2 through 5
                 */
                const overlapEndOffsetInPiece = Math.min(edit.endOffset, overlappingAcceptedPiece.endOffset) - overlappingAcceptedPiece.startOffset;
                const isolatedDeletedRun = this.isolatePieceCharacterRangeInRun(overlappingAcceptedPiece, { fromOffsetInPiece: overlapStartOffsetInPiece, toOffsetInPiece: overlapEndOffsetInPiece });
                const deletedCharacterCount = overlapEndOffsetInPiece - overlapStartOffsetInPiece;
                /**
                 * WHEN this deleted span is our own insertion and no foreign revision encloses it
                 *   CONSEQUENCE
                 *     Vacate the run instead of wrapping it in w:del.
                 *     This matches Word's "delete your own insertion" behavior.
                 */
                if (collapsesIntoOwnInsertion(overlappingAcceptedPiece, isolatedDeletedRun)) {
                    struckRuns.push({ placementAnchor: removeNonPropertyChildrenFromOwnInsertionRun(isolatedDeletedRun, overlappingAcceptedPiece), formattingSourceRun: isolatedDeletedRun, deletedCharacterCount, vacatedOwnInsertion: true });
                    /**
                     * CONSEQUENCE
                     *   Skip w:del conversion and wrapping for this run: vacate() already handled it.
                     */
                    continue;
                }
                toDeletedText(isolatedDeletedRun, overlappingAcceptedPiece);
                struckRuns.push({ placementAnchor: wrapInDeletion(isolatedDeletedRun), formattingSourceRun: isolatedDeletedRun, deletedCharacterCount, vacatedOwnInsertion: false });
            }
            return struckRuns;
        };

        // Vacated runs stay in the tree until replacement text has been placed after them, since they are its anchor.
        const dropVacatedRuns = (struck: StruckRun[]): void => {
            for (const struckRun of struck) {
                /**
                 * CASES
                 *   - vacatedOwnInsertion is true   -> remove the emptied run now
                 *   - vacatedOwnInsertion is false  -> keep it as tracked deletion markup
                 */
                if (!struckRun.vacatedOwnInsertion) continue;
                const anchorParentElement = struckRun.placementAnchor.parentElement;
                struckRun.placementAnchor.parentNode?.removeChild(struckRun.placementAnchor);
                /**
                 * WHEN removing the vacated run leaves its insertion container empty
                 *   CONSEQUENCE
                 *     Remove the empty w:ins as leftover scaffolding.
                 */
                if (anchorParentElement && INSERTION_CONTAINERS.has(elementNameWithoutPrefix(anchorParentElement)) && anchorParentElement.children.length === 0) {
                    anchorParentElement.parentNode?.removeChild(anchorParentElement);
                }
            }
        };

        // Consecutive struck runs in the same structural container form one home; the
        // last run of the group is where that home's share of new text is placed.
        /**
         * SCOPE
         *   Local to applyEdit.
         *   This grouping exists only for the current replacement.
         *
         * CONSEQUENCE
         *   - merge consecutive struck runs from the same structural home
         *   - produce one insertion destination per touched container
         */
        const mergeDeletionRunsIntoStructuralHomes = (struck: StruckRun[]): DeletionHome[] => {
            const homeOf = (node: Element): Element => {
                for (let ancestorElement = node.parentElement; ancestorElement && ancestorElement !== paragraphElement; ancestorElement = ancestorElement.parentElement) {
                    /**
                     * CASES
                     *   - nearest ancestor is a structural home
                     *       -> return that container
                     *   - no structural home before the paragraph
                     *       -> the paragraph is the home
                     */
                    if (STRUCTURAL_HOMES.has(elementNameWithoutPrefix(ancestorElement))) return ancestorElement;
                }
                return paragraphElement;
            };
            /**
             * TYPE
             *   DeletionHome plus its structural home container.
             *
             * WHY
             *   The extra container field is the identity used while merging.
             *   Only consecutive struck runs from the same structural home merge.
             */
            const mergedDeletionHomes: Array<DeletionHome & { container: Element }> = [];
            for (const struckRun of struck) {
                const structuralHomeContainer = homeOf(struckRun.placementAnchor);
                /**
                 * EXAMPLE
                 *   homes.length       = 3
                 *   homes.length - 1   = 2
                 *   homes[2]           -> third and last merged home
                 */
                const lastMergedHome = mergedDeletionHomes[mergedDeletionHomes.length - 1];
                /**
                 * CASES
                 *   - no home merged yet                          -> no: this run opens the first home
                 *   - the last home sits in the same container    -> yes: the run joins it
                 *   - it sits in another container (a hyperlink)  -> no: a new home opens
                 */
                const struckRunBelongsToLastMergedHome = (): boolean => {
                    const lastMergedHomeExists = !!lastMergedHome;
                    if (!lastMergedHomeExists) return false;
                    return lastMergedHome.container === structuralHomeContainer;
                };
                /**
                 * WHEN this struck run is in the same structural container as the last home
                 *   CONSEQUENCE
                 *     Merge it into that home instead of starting a new destination.
                 */
                if (struckRunBelongsToLastMergedHome()) {
                    /**
                     * WHEN this struck run stays in the same structural home as the last home
                     *   CONSEQUENCE
                     *     Add its deleted count onto that home instead of starting another
                     *     replacement destination.
                     */
                    lastMergedHome.placementAnchor = struckRun.placementAnchor;
                    /**
                     */
                    lastMergedHome.deletedCharacterCount += struckRun.deletedCharacterCount;
                } else {
                    mergedDeletionHomes.push({
                        container: structuralHomeContainer,
                        placementAnchor: struckRun.placementAnchor,
                        formattingSourceRun: struckRun.formattingSourceRun,
                        deletedCharacterCount: struckRun.deletedCharacterCount,
                    });
                }
            }
            return mergedDeletionHomes;
        };

        // Split the replacement on word boundaries in proportion to the characters
        // each home lost, every home getting at least one word when there are
        // enough to go round. A deletion that ran from inside a content control
        // into the text after it must put words back inside the control, or Word
        // drops the emptied control together with its binding.
        /**
         * SCOPE
         *   Local to applyEdit.
         *   Runs after deletion homes are known.
         *
         * CONSEQUENCE
         *   Split this edit's replacement into one contiguous text share per merged
         *   home, preserving every touched structural container.
         */
        const distributeReplacementTextAcrossDeletionHomes = (homes: DeletionHome[]): string[] => {
            const wordsWithTrailingSpace = edit.replacementText.split(/(?<=\S)(?=\s)/);
            const onlyOneDeletionHomeExists = homes.length === 1;
            const replacementHasFewerWordsThanDeletionHomes = wordsWithTrailingSpace.length < homes.length;
            const replacementTextShouldStayInFirstHome = onlyOneDeletionHomeExists || replacementHasFewerWordsThanDeletionHomes;
            /**
             * EXAMPLE
             *   homes.length = 3
             *   homes.slice(1) -> homes 1 and 2
             *   result        -> [full replacement, "", ""]
             *
             * CASES
             *   - exactly one home                 -> first home gets all text
             *   - fewer words than homes           -> first home gets all text
             *   - otherwise                        -> distribute by deleted size
             */
            /**
             * WHEN there is only one home, or fewer word-sized units than homes
             *   CONSEQUENCE
             *     Put the full replacement in the first home.
             *     Later homes get empty shares.
             *
             * WHY
             *   Do not split into fragmentary tokens while an earlier touched container
             *   gets only part of a word.
             */
            if (replacementTextShouldStayInFirstHome) return [edit.replacementText, ...homes.slice(1).map(() => "")];
            const deletedTotal = homes.reduce((sum, home) => sum + home.deletedCharacterCount, 0);
            const replacementTextSharesByHome: string[] = [];
            let wordsTaken = 0;
            /**
             * SCOPE
             *   homeIndex walks merged deletion homes in document order.
             *   That is the same order produced by merging the struck runs from this
             *   deletion scan.
             */
            for (let homeIndex = 0; homeIndex < homes.length; homeIndex++) {
                /**
                 * EXAMPLE
                 *   homes.length               = 4
                 *   homeIndex                  = 1
                 *   4 - 1 - 1                  -> 2
                 *   remaining homes            -> homes 2 and 3
                 */
                const remainingHomesAfterCurrent = homes.length - homeIndex - 1;
                const maximumWordsCurrentHomeMayTake = wordsWithTrailingSpace.length - wordsTaken - remainingHomesAfterCurrent;
                const proportionalWordCountBeforeRounding = (wordsWithTrailingSpace.length * homes[homeIndex].deletedCharacterCount) / deletedTotal;
                const proportionalWordCountForCurrentHome = Math.round(proportionalWordCountBeforeRounding);
                const minimumOneWordCountForCurrentHome = Math.max(1, proportionalWordCountForCurrentHome);
                /**
                 * EXAMPLE
                 *   homes.length       = 4
                 *   homes.length - 1   = 3
                 *   index === 3        -> fourth home takes all remaining words
                 */
                /**
                 * CASES
                 *   - homeIndex === homes.length - 1
                 *       -> last home takes every remaining word
                 *   - otherwise
                 *       -> cap at totalWords - wordsTaken - remainingHomesAfterCurrent
                 *          so each later home keeps one word reserved
                 *
                 * EXAMPLE
                 *   totalWords                  = 6
                 *   wordsTaken                  = 2
                 *   remainingHomesAfterCurrent  = 2
                 *   6 - 2 - 2                  -> 2
                 *   this home may take at most 2 words
                 */
                const wordCountForCurrentHome = homeIndex === homes.length - 1
                    ? wordsWithTrailingSpace.length - wordsTaken
                    : Math.min(
                        minimumOneWordCountForCurrentHome,
                        maximumWordsCurrentHomeMayTake,
                    );
                /**
                 * EXAMPLE
                 *   wordsWithTrailingSpace = ["forty ", "five ", "days "], wordsTaken = 1, wordCountForCurrentHome = 2
                 *   slice(1, 3) -> ["five ", "days "]: this home's share, starting where the previous home stopped
                 */
                const replacementWordsForCurrentHome = wordsWithTrailingSpace.slice(wordsTaken, wordsTaken + wordCountForCurrentHome);
                const replacementTextShareForCurrentHome = replacementWordsForCurrentHome.join("");
                /**
                 * EXAMPLE
                 *   wordsTaken               = 2
                 *   wordCount                = 3
                 *   slice(2, 5)              -> entries 2, 3, 4
                 *   join("")                 -> this home's contiguous text share
                 */
                replacementTextSharesByHome.push(replacementTextShareForCurrentHome);
                wordsTaken += wordCountForCurrentHome;
            }
            return replacementTextSharesByHome;
        };

        // Pure insertion: formatting comes from the run the text lands in, or the last run when appending.
        /**
         * SCOPE
         *   Local to applyEdit.
         *   Covers only pure-insertion cases for this edit.
         *
         * CASES
         *   - before the first accepted piece
         *   - inside a containing piece
         *   - after the last accepted run
         *
         * CONSEQUENCE
         *   Use the live run at the landing point as the formatting source.
         */
        const insertReplacementAtAcceptedOffset = (): void => {
            const ownerDocument = paragraphElement.ownerDocument as Document;
            const acceptedPieces = model.acceptedPieces();
            /**
             * WHEN the paragraph has no accepted-text pieces
             *   CONSEQUENCE
             *     A bare insertion wrapper, with no existing run to copy or place beside,
             *     goes inside the paragraph's own bookmark, so the bookmark covers the new
             *     words (`OoxmlBookmarks.paragraphBookmarkEndWithin`).
             *
             * CASES
             *   - the paragraph's bookmark ends inside it -> right before that end
             *   - it does not (null)                      -> appended: `insertBefore` with a null
             *                                                reference node appends
             */
            if (acceptedPieces.length === 0) {
                paragraphElement.insertBefore(this.insertionOf({ formattingSourceRun: null, doc: ownerDocument, text: edit.replacementText, writtenAsLink }), OoxmlBookmarks.paragraphBookmarkEndWithin(paragraphElement));
                return;
            }
            /**
             * EXAMPLE
             *   live[0].startOffset = 5
             *   edit.startOffset    = 3
             *   3 <= 5              -> insertion lands before the first live piece
             *
             * WHEN the insertion offset is at or before the first accepted-text piece
             *   CONSEQUENCE
             *     Anchor the new insertion in front of the paragraph's current content.
             */
            if (edit.startOffset <= acceptedPieces[0].startOffset) {
                /**
                 * EXAMPLE
                 *   live.length = 4
                 *   live[0]     -> entry 0, the earliest piece in document order
                 */
                const firstAcceptedRunAtInsertionPoint = this.peelHead(acceptedPieces[0]);
                this.placeInsertion({ wrapper: this.insertionOf({ formattingSourceRun: firstAcceptedRunAtInsertionPoint, doc: ownerDocument, text: edit.replacementText, writtenAsLink }), container: firstAcceptedRunAtInsertionPoint.parentNode as Node, beforeNode: firstAcceptedRunAtInsertionPoint });
                return;
            }
            for (const acceptedPiece of acceptedPieces) {
                /**
                 * WHEN this piece ends at or before the insertion offset
                 *   CONSEQUENCE
                 *     Skip it and keep scanning for the containing piece or the first piece
                 *     after the offset.
                 */
                if (edit.startOffset >= acceptedPiece.endOffset) continue;
                const formattingSourceRun = acceptedPiece.run;
                const offsetInPiece = edit.startOffset - acceptedPiece.startOffset;
                const insertionWrapper = this.insertionOf({ formattingSourceRun: formattingSourceRun, doc: ownerDocument, text: edit.replacementText, writtenAsLink });
                /**
                 * WHEN the insertion lands on this piece's left boundary
                 *   CONSEQUENCE
                 *     Do not split text. Place the wrapper directly before this piece's
                 *     run content.
                 */
                if (offsetInPiece === 0) {
                    const pieceHeadRun = this.peelHead(acceptedPiece);
                    /**
                     * WHEN the insertion lands inside this piece
                     *   CONSEQUENCE
                     *     Split the run at the offset and place the wrapper between the two
                     *     halves.
                     */
                    this.placeInsertion({ wrapper: insertionWrapper, container: pieceHeadRun.parentNode as Node, beforeNode: pieceHeadRun });
                } else {
                    const trailingRunAfterSplit = this.splitAfter(acceptedPiece, offsetInPiece);
                    this.placeInsertion({ wrapper: insertionWrapper, container: trailingRunAfterSplit.parentNode as Node, beforeNode: trailingRunAfterSplit });
                }
                return;
            }
            /**
             * EXAMPLE
             *   live.length       = 5
             *   live.length - 1   = 4
             *   live[4].run       -> fifth and last live run
             */
            const lastAcceptedRun = acceptedPieces[acceptedPieces.length - 1].run;
            /**
             * CASES
             *   - endOffset > startOffset   -> strikeOrVacateDeletionSpan()
             *       replacement or deletion over existing accepted text
             *   - endOffset <= startOffset  -> []
             *       no accepted-text span to strike, so zero-length and reversed ranges
             *       behave as insertion-only here
             */
            this.placeInsertion({ wrapper: this.insertionOf({ formattingSourceRun: lastAcceptedRun, doc: ownerDocument, text: edit.replacementText, writtenAsLink }), container: lastAcceptedRun.parentNode as Node, beforeNode: lastAcceptedRun.nextSibling });
        };

        /**
         * CASES
         *   - the edit covers accepted text (end past start) -> strike or vacate that span
         *   - it covers none (a pure insertion)              -> nothing struck
         */
        const struckRuns = edit.endOffset > edit.startOffset ? strikeOrVacateDeletionSpan() : [];
        /**
         * WHEN replacementText is empty
         *   CONSEQUENCE
         *     This edit is a pure deletion.
         *     Drop any vacated runs and stop: no insertion markup is added back.
         */
        if (edit.replacementText.length === 0) {
            dropVacatedRuns(struckRuns);
            return;
        }
        /**
         * WHEN some existing text was struck or vacated
         *   CONSEQUENCE
         *     Treat this as a replacement: share the new text across the deletion
         *     homes that lost content.
         */
        if (struckRuns.length > 0) {
            const deletionHomesByContainer = mergeDeletionRunsIntoStructuralHomes(struckRuns);
            const replacementTextSharesByHome = distributeReplacementTextAcrossDeletionHomes(deletionHomesByContainer);
            deletionHomesByContainer.forEach((home, index) => {
                /**
                 * WHEN this home's assigned share is empty
                 *   CONSEQUENCE
                 *     Skip insertion for that home.
                 */
                if (replacementTextSharesByHome[index].length === 0) return;
                const insertionWrapper = this.insertionOf({ formattingSourceRun: home.formattingSourceRun, doc: home.placementAnchor.ownerDocument as Document, text: replacementTextSharesByHome[index], writtenAsLink });
                this.placeInsertion({ wrapper: insertionWrapper, container: home.placementAnchor.parentNode as Node, beforeNode: home.placementAnchor.nextSibling });
            });
            dropVacatedRuns(struckRuns);
            return;
        }
        insertReplacementAtAcceptedOffset();
    }

    /**
     * Writes a pure insertion (an edit that covers no character) in front of
     * `elementBeforeWhichToInsert`, in that element's own container, its formatting
     * taken from the live run that ends where the insertion goes.
     *
     * WHY
     *   `applyEdit` puts a pure insertion in front of the run its offset starts, inside
     *   that run's container. At a link's start that run is the link's first, so the
     *   words went into the link. The engine hands a link's start here with the element
     *   that opens the link (`LinkFieldStriker.linkStartsOf`), so the words go in front
     *   of the link, as they go after it at its end.
     *
     * FALLBACK
     *   No live run ends at the insertion (the link opens the paragraph): the insertion
     *   carries no formatting of its own, as one at an empty paragraph's start does.
     *
     * EXAMPLE
     *   "see Terms now", insertion "the " at 4, the link's begin run
     *   -> <w:ins>the </w:ins> in front of the begin run, formatted as "see "
     *
     * CASES, by `writtenAsLink`: as `applyEdit`.
     */
    insertBefore(edit: PlannedEdit, elementBeforeWhichToInsert: Element, writtenAsLink: ReplacementLinkOfStruckLink | null): void {
        const ownerDocument = this.paragraphModel.paragraph.ownerDocument as Document;
        const runEndingAtInsertion = this.paragraphModel.acceptedPieces().find((acceptedPiece) => acceptedPiece.endOffset === edit.startOffset)?.run ?? null;
        this.placeInsertion({
            wrapper: this.insertionOf({ formattingSourceRun: runEndingAtInsertion, doc: ownerDocument, text: edit.replacementText, writtenAsLink }),
            container: elementBeforeWhichToInsert.parentNode as Node,
            beforeNode: elementBeforeWhichToInsert,
        });
    }

    /**
     * Cuts the runs so that the accepted characters `startOffset..endOffset`
     * (half-open) sit in runs of their own, and returns those runs in document
     * order, so a caller can mark exactly those characters: the drawer marks the
     * words a comment sits on this way. Nothing is struck, inserted or restored.
     *
     * EXAMPLE
     *   accepted text  "18% of the subscription fee", one run
     *   range          11..27, the characters of "subscription fee"
     *   -> the run is split into "18% of the " and "subscription fee"; the second run is returned
     *
     * CASES
     *   - the range starts and ends inside one piece        -> that piece's run is split twice, the middle run returned
     *   - the range spans several pieces                    -> the first and last are split at the range's edges, every
     *                                                          piece between is returned whole
     *   - a struck piece sits inside the range              -> skipped: accepted offsets never fall on deleted text
     *
     * RETURNS
     *   [] when no accepted piece overlaps the range: an empty range, or offsets past the text.
     */
    isolateAcceptedCharacterRange(startOffset: number, endOffset: number): Element[] {
        /**
         * WHEN the range holds no character
         *   CONSEQUENCE
         *     nothing to isolate: a cut here would only leave an empty run behind
         */
        if (endOffset <= startOffset) return [];
        /**
         * CASES
         *   - piece.startOffset < endOffset and piece.endOffset > startOffset -> the piece holds characters of the range
         *   - the piece only touches the range at one boundary             -> left out
         */
        const acceptedPiecesInRange = this.paragraphModel.acceptedPieces().filter((piece) => piece.startOffset < endOffset && piece.endOffset > startOffset);
        return acceptedPiecesInRange.map((pieceInRange) => {
            /**
             * EXAMPLE
             *   startOffset             = 11
             *   pieceInRange.startOffset = 0
             *   max(11, 0) - 0          -> 11: the range starts 11 characters into this piece
             */
            const fromOffsetInPiece = Math.max(startOffset, pieceInRange.startOffset) - pieceInRange.startOffset;
            /**
             * EXAMPLE
             *   endOffset              = 27
             *   pieceInRange.endOffset = 27
             *   min(27, 27) - 0        -> 27: the range runs to the end of this piece
             */
            const toOffsetInPiece = Math.min(endOffset, pieceInRange.endOffset) - pieceInRange.startOffset;
            return this.isolatePieceCharacterRangeInRun(pieceInRange, { fromOffsetInPiece, toOffsetInPiece });
        });
    }

    /**
     * Writes this author's copy of the struck runs `pieces` sit in, right after
     * the deletion holding them: the one write of a revert of another author's
     * deletion. Their `w:del` stays as it is, so Reject All gives their document
     * back, and Accept All gives the words back in the formatting they had, since
     * the copy is their runs and not new text.
     *
     * CASES, per `w:del` the pieces sit in: one insertion each, placed right after
     * that `w:del`, so a struck run inside a `w:hyperlink` or a content control
     * gets its copy inside the same link or control
     *   - a run of ordinary struck text                         -> cloned: attributes and `rPr` as `shellOf` copies them,
     *                                                              `w:delText` as `w:t`, tabs and breaks as they are
     *   - a run holding a comment reference mark                -> left out: the comment is anchored once already
     *   - a complete field Word does not redraw (HYPERLINK)      -> cloned whole, markers included, `w:delInstrText` as
     *                                                              `w:instrText`, so the link works again
     *   - a complete field Word redraws (REF, PAGE, DATE, TOC)   -> its result runs alone, as ordinary runs: the rule
     *                                                              `WordOwnedTextUnlinker` applies to text an edit reaches
     *   - a field only partly inside the deletion                -> its result runs alone; half a field is no field
     *   - the deletion sits inside another author's insertion    -> the copy goes beside the deletion the way `placeInsertion`
     *                                                              places text: the outer `w:ins` is split, never nested in
     *
     * RETURNS
     *   The copied runs in document order, so a caller can find the restored words once the model refreshes.
     */
    insertCopyOfStruckRuns(pieces: TextPiece[]): Element[] {
        const deletions: Element[] = [];
        for (const piece of pieces) {
            const deletion = this.deletionHolding(piece.run);
            /**
             * WHEN two pieces sit in one deletion (a run holding text and a tab, or two runs of one `w:del`)
             *   CONSEQUENCE
             *     the deletion is copied once.
             */
            if (!deletions.includes(deletion)) deletions.push(deletion);
        }
        const copiedRuns: Element[] = [];
        for (const deletion of deletions) {
            /**
             * CASES
             *   - a `w:del` container -> every run inside it, in document order: the text runs the pieces
             *                            name, and with them the field markers, instruction runs and
             *                            comment marks that carry no text piece of their own
             *   - a run holding its own deletion (an equation's run) -> that run alone
             */
            const struckRunsOfDeletion = DELETION_CONTAINERS.has(elementNameWithoutPrefix(deletion))
                ? Array.from(deletion.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r"))
                : [deletion];
            const copies = this.restoredCopiesOf(struckRunsOfDeletion);
            /**
             * WHEN nothing of the deletion is copied (only a comment reference mark was struck)
             *   CONSEQUENCE
             *     no empty insertion is written.
             */
            if (copies.length === 0) continue;
            const insertionWrapper = this.appliedRevision.stamp((deletion.ownerDocument as Document).createElementNS(WORDPROCESSINGML_NS, "w:ins"));
            for (const copy of copies) insertionWrapper.appendChild(copy);
            this.placeInsertion({ wrapper: insertionWrapper, container: deletion.parentNode as Node, beforeNode: deletion.nextSibling });
            copiedRuns.push(...copies);
        }
        return copiedRuns;
    }

    /**
     * The `w:del` (or `w:moveFrom`) nearest above `run`, the element the copy is
     * placed after; the run itself when the deletion was written inside it, which
     * is how Word strikes an equation's run.
     */
    deletionHolding(run: Element): Element {
        for (let ancestor: Element | null = run.parentElement; ancestor && ancestor !== this.paragraphModel.paragraph; ancestor = ancestor.parentElement) {
            if (DELETION_CONTAINERS.has(elementNameWithoutPrefix(ancestor))) return ancestor;
        }
        return run;
    }

    /**
     * The live copies of one deletion's struck runs, in order, by the field rule
     * in `insertCopyOfStruckRuns`: a run outside every field, and a field's result
     * runs, are copied; a field's marker and instruction runs come along only when
     * the field is complete and one Word does not redraw; a comment reference run
     * is left out.
     *
     * EXAMPLE
     *   struck runs  [begin] [instr "HYPERLINK …"] [separate] ["our site"] [end] [" and more"]
     *   -> every run copied: the link comes back whole
     *   struck runs  [begin] [instr "REF _Ref1"] [separate] ["Section 5"] [end]
     *   -> ["Section 5"] alone, as ordinary text
     */
    private restoredCopiesOf(struckRuns: Element[]): Element[] {
        const fieldWalk = complexFieldWalkOf(this.paragraphModel.paragraph);
        const struckRunSet = new Set(struckRuns);
        /**
         * The field a struck run belongs to: the one it marks, else the innermost field
         * open over it; undefined for a run outside every field.
         *
         * EXAMPLE
         *   fields open over a link's text inside a contents entry = [the TOC, the HYPERLINK]
         *   `length - 1` -> 1, the HYPERLINK, the innermost
         */
        const fieldOfStruckRun = (struckRun: Element): ComplexField | undefined => {
            const fieldsOpenOverRun = fieldWalk.fieldsOpenOverRun.get(struckRun) ?? [];
            return fieldWalk.fieldOfMarkerRun.get(struckRun) ?? fieldsOpenOverRun[fieldsOpenOverRun.length - 1]?.field;
        };
        /**
         * Whether a field comes back whole with the copy: both its begin and its end are
         * among the struck runs, and it is one Word does not redraw (a HYPERLINK), so the
         * link works again. Half a field is no field, and a field Word redraws comes back
         * as its result text alone.
         *
         * EXAMPLE
         *   runsBeginToEnd [begin, instr, separate, "our site", end], all struck -> `length - 1` is 4, the end run: whole
         *   the same field with its end in a later paragraph                     -> not complete: result text alone
         */
        const fieldComesBackWhole = (field: ComplexField): boolean =>
            field.complete
            && struckRunSet.has(field.runsBeginToEnd[0])
            && struckRunSet.has(field.runsBeginToEnd[field.runsBeginToEnd.length - 1])
            && !fieldResultIsRedrawnByWord(field.fieldInstruction);
        const copies: Element[] = [];
        for (const struckRun of struckRuns) {
            const holdsCommentReference = Array.from(struckRun.children).some((child) => elementNameWithoutPrefix(child) === COMMENT_REFERENCE_TAG);
            if (holdsCommentReference) continue;
            const field = fieldOfStruckRun(struckRun);
            const isMarkerRun = field !== undefined && field.markerRuns.includes(struckRun);
            /**
             * WHEN the run is a field's marker or instruction and the field is not one that comes back whole
             *   CONSEQUENCE
             *     left out; its result runs are copied as ordinary text.
             */
            if (isMarkerRun && field !== undefined && !fieldComesBackWhole(field)) continue;
            copies.push(this.restoredCopyOf(struckRun));
        }
        return copies;
    }

    /**
     * One struck run copied as live text: the shell `shellOf` makes (attributes,
     * `rPr` without its `rPrChange`), then every other child in its live spelling.
     */
    private restoredCopyOf(struckRun: Element): Element {
        const copy = this.shellOf(struckRun);
        for (const child of Array.from(struckRun.children)) {
            if (elementNameWithoutPrefix(child) === "rPr") continue;
            ParagraphSplicer.appendLiveCopyOf(child, copy);
        }
        return copy;
    }

    /**
     * Appends to `parent` a copy of one struck run child in its live spelling.
     *
     * CASES
     *   - `w:delText` / `w:delInstrText` -> a `w:t` / `w:instrText` with the same attributes and text
     *   - a deletion container inside the run (an equation's run) -> its children, each copied the same way; the container goes
     *   - anything else (a tab, a break, a field char, a drawing) -> a deep copy as it is
     */
    private static appendLiveCopyOf(struckChild: Element, parent: Element): void {
        const tag = elementNameWithoutPrefix(struckChild);
        if (DELETION_CONTAINERS.has(tag)) {
            for (const deletedGrandchild of Array.from(struckChild.children)) ParagraphSplicer.appendLiveCopyOf(deletedGrandchild, parent);
            return;
        }
        const liveTag = LIVE_SPELLING_OF_STRUCK_CHILD[tag];
        if (liveTag === undefined) {
            parent.appendChild(struckChild.cloneNode(true));
            return;
        }
        const liveChild = RunSpelling.elementLike(struckChild, liveTag);
        for (const attribute of Array.from(struckChild.attributes)) liveChild.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
        ParagraphSplicer.setText(liveChild, struckChild.textContent ?? "");
        parent.appendChild(liveChild);
    }

    /**
     * Un-deletes ranges of our own earlier deletions. Each range is cut into its
     * own run, its `w:delText` turned back into `w:t`, and the run lifted out of
     * the `w:del` that wrapped it, splitting that container when the restored
     * run sat in its middle. Ranges are processed last to first so each split
     * leaves the remaining offsets intact, the same discipline as `applyEdit`.
     */
    restoreDeletions(ranges: RestorableRange[]): void {
        // `w:delText` back to `w:t`: the characters are live again and Word must render them as text, not as struck text.
        const toRestoredText = (run: Element): void => {
            const ownerDocument = run.ownerDocument as Document;
            for (const runChildElement of Array.from(run.children)) {
                /**
                 * CASES
                 *   - child is w:delText      -> restore it to w:t
                 *   - child is anything else  -> leave it unchanged
                 */
                if (elementNameWithoutPrefix(runChildElement) !== "delText") continue;
                const restoredTextElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:t");
                ParagraphSplicer.setText(restoredTextElement, runChildElement.textContent ?? "");
                run.replaceChild(restoredTextElement, runChildElement);
            }
        };
        // The restored run must leave the `w:del` or it would still read as deleted.
        // Whatever followed it inside the deletion moves into a fresh `w:del` with
        // its own id, so the original deletion stays one revision before the run
        // and another after it, and an emptied container is removed.
        const liftOutOfDeletion = (run: Element): void => {
            /**
             * WHEN the deletion was written inside the run (an equation's run)
             *   CONSEQUENCE
             *     unwrap it there: its children move back onto the run and the container goes,
             *     which is the whole restoration, since the range was already isolated into
             *     this run of its own.
             */
            const deletionInsideRun = Array.from(run.children).find((child) => DELETION_CONTAINERS.has(elementNameWithoutPrefix(child)));
            if (deletionInsideRun) {
                for (const deletedChild of Array.from(deletionInsideRun.children)) run.insertBefore(deletedChild, deletionInsideRun);
                run.removeChild(deletionInsideRun);
                return;
            }
            const deletionContainer = run.parentElement;
            const runHasDeletionContainerParent = !!deletionContainer;
            const runIsInsideDeletionContainer = runHasDeletionContainerParent && DELETION_CONTAINERS.has(elementNameWithoutPrefix(deletionContainer));
            /**
             * WHEN the run is not inside a deletion container
             *   CONSEQUENCE
             *     Nothing to lift out. Its structure is already restored.
             */
            if (!runIsInsideDeletionContainer) return;
            const deletionContainerParentNode = deletionContainer.parentNode as Node;
            const trailingDeletionContainer = this.splitContainerBefore(deletionContainer, run.nextSibling);
            deletionContainerParentNode.insertBefore(run, deletionContainer.nextSibling);
            /**
             * WHEN splitContainerBefore produced a trailing deletion half
             *   CONSEQUENCE
             *     Reinsert that tail after the restored run.
             */
            if (trailingDeletionContainer) deletionContainerParentNode.insertBefore(trailingDeletionContainer, run.nextSibling);
            /**
             * WHEN lifting out the restored run leaves the original deletion wrapper empty
             *   CONSEQUENCE
             *     Remove the empty wrapper.
             */
            if (!deletionContainer.hasChildNodes()) deletionContainerParentNode.removeChild(deletionContainer);
        };
        const documentOrder = (first: Node, second: Node): number => {
            /**
             * WHEN both references point to the same DOM node
             *   CONSEQUENCE
             *     Their document order is equal here. Any tie-break happens later on
             *     local offsets within that node.
             */
            if (first === second) return 0;
            const firstNodePositionRelativeToSecond = first.compareDocumentPosition(second);
            const firstNodeComesBeforeSecond = (firstNodePositionRelativeToSecond & DOCUMENT_POSITION_FOLLOWING) !== 0;
            /**
             * RETURNS
             *   - -1 when first comes before second in document order
             *   -  1 when first comes after second
             */
            return firstNodeComesBeforeSecond ? -1 : 1;
        };

        /**
         * SCOPE
         *   Ordered from later document position to earlier.
         *   Within one text node, ordered from higher local offset to lower.
         *
         * WHY
         *   Restoration splits must run back to front.
         *   -> Every remaining RestorableRange coordinate stays valid.
         */
        const rangesInReverseDocumentOrder = [...ranges].sort((first, second) => {
            const textNodeDocumentOrder = documentOrder(first.piece.textNode, second.piece.textNode);
            /**
             * WHY
             *   Process restorations from paragraph end toward start.
             *   Within one text node, process higher local offsets before lower ones.
             *   -> Each split leaves earlier coordinates valid.
             */
            return textNodeDocumentOrder !== 0 ? -textNodeDocumentOrder : second.fromOffsetInPiece - first.fromOffsetInPiece;
        });
        for (const restorableRange of rangesInReverseDocumentOrder) {
            const isolatedRestorableRun = this.isolatePieceCharacterRangeInRun(restorableRange.piece, restorableRange);
            toRestoredText(isolatedRestorableRun);
            liftOutOfDeletion(isolatedRestorableRun);
        }
    }

    /** True when a revision container by someone other than this revision's author encloses the piece. */
    wrappedByAnotherAuthor(piece: TextPiece): boolean {
        for (let ancestorElement = piece.textNode.parentElement; ancestorElement && ancestorElement !== this.paragraphModel.paragraph; ancestorElement = ancestorElement.parentElement) {
            const ancestorElementIsRevisionContainer = REVISION_CONTAINERS.has(elementNameWithoutPrefix(ancestorElement));
            const ancestorElementIsByAppliedRevisionAuthor = ancestorElementIsRevisionContainer ? this.appliedRevision.isBy(ancestorElement.getAttributeNS(WORDPROCESSINGML_NS, "author")) : false;
            const ancestorElementIsForeignRevisionContainer = ancestorElementIsRevisionContainer && !ancestorElementIsByAppliedRevisionAuthor;
            /**
             * WHEN an enclosing revision container belongs to another author
             *   TRUE MEANS
             *     This piece is wrapped by another author's tracked change.
             */
            if (ancestorElementIsForeignRevisionContainer) return true;
        }
        return false;
    }

    /** Whether `node` is `ancestor` itself or sits anywhere below it; the DOM's `contains`, spelled out because xmldom's nodes carry none. */
    private static holds(ancestor: Element, node: Node): boolean {
        for (let current: Node | null = node; current; current = current.parentNode) {
            if (current === ancestor) return true;
        }
        return false;
    }

    /** Writes text into a `w:t`/`w:delText` with `xml:space="preserve"`, since every run may begin or end with the space that separates words. */
    private static setText(node: Element, text: string): void {
        node.textContent = text;
        node.setAttributeNS(XML_NS, "xml:space", "preserve");
    }

    /**
     * Where a copy of the piece's run should take its content: the copied run
     * itself when the text sits directly in the run, and a fresh copy of the
     * revision container when the text sits inside a `w:ins` or `w:del` written
     * inside the run, which is how Word marks a tracked change in an equation.
     * The copied container takes the next revision id and the container's own
     * run properties, so each half of a split change is a revision in its own
     * right, the same way `splitContainerBefore` splits an ordinary one.
     */
    private contentParentFor(piece: TextPiece, runCopy: Element): Element {
        const sourceContentParent = piece.textNode.parentElement as Element;
        /**
         * WHEN the text node is a direct child of its run
         *   CONSEQUENCE
         *     ordinary text: the copied run holds the content itself.
         */
        if (sourceContentParent === piece.run) return runCopy;
        const containerCopy = sourceContentParent.cloneNode(false) as Element;
        containerCopy.setAttributeNS(WORDPROCESSINGML_NS, "w:id", this.appliedRevision.takeNextRevisionId());
        for (const containerRunProperties of RunSpelling.runPropertiesOf(sourceContentParent)) {
            containerCopy.appendChild(containerRunProperties.cloneNode(true) as Element);
        }
        runCopy.appendChild(containerCopy);
        return containerCopy;
    }

    /**
     * An empty copy of `isolatedDeletedRun` carrying its attributes and run properties. The
     * `rPrChange` record is dropped: it describes a formatting change someone
     * made to the original run, and repeating it on a new run would show a
     * phantom formatting revision.
     */
    private shellOf(run: Element): Element {
        /** The shell is written in the run's own spelling, so a copy of an equation's run is an `m:r`, not a `w:r`. */
        const runShell = RunSpelling.elementLike(run, elementNameWithoutPrefix(run));
        for (const runAttribute of Array.from(run.attributes)) {
            runShell.setAttributeNS(runAttribute.namespaceURI, runAttribute.name, runAttribute.value);
        }
        /**
         * WHY every `rPr`, not the first
         *   An equation's run carries two: `m:rPr` for the math styling and `w:rPr` for the
         *   character formatting. A copy that took one of them would lose the other.
         */
        for (const runPropertiesElement of RunSpelling.runPropertiesOf(run)) {
            const clonedRunPropertiesElement = runPropertiesElement.cloneNode(true) as Element;
            for (const runPropertyChildElement of Array.from(clonedRunPropertiesElement.children)) {
                /**
                 * WHEN a cloned run-property child is rPrChange
                 *   CONSEQUENCE
                 *     Drop that formatting-revision record so the new run does not replay
                 *     an older formatting change as a fresh one.
                 */
                if (elementNameWithoutPrefix(runPropertyChildElement) === "rPrChange") clonedRunPropertiesElement.removeChild(runPropertyChildElement);
            }
            runShell.appendChild(clonedRunPropertiesElement);
        }
        return runShell;
    }

    /**
     * Splits the run at `offsetInPiece` characters into `piece`. The characters
     * before the offset stay in the original run; the rest, plus every sibling
     * node after the text node, move to a new run inserted right after it.
     * Returns that tail run. A glyph piece has one character, so it is never
     * cut, only its following siblings move.
     */
    private splitAfter(piece: TextPiece, offsetInPiece: number): Element {
        const sourceRun = piece.run;
        const trailingRun = this.shellOf(sourceRun);
        /**
         * Where the tail's nodes go: the tail run itself for ordinary text, and a
         * copy of the revision container for a tracked change written inside an
         * equation's run, so the tail stays the same author's change.
         */
        const trailingContentParent = this.contentParentFor(piece, trailingRun);

        /**
         * CASES
         *   - piece is text   -> split by character offset
         *   - piece is glyph  -> do not cut it; only later siblings can move
         */
        if (!piece.isGlyph) {
            const pieceText = piece.text;
            /**
             * EXAMPLE
             *   whole          = "draft"
             *   offsetInPiece  = 2
             *   slice(0, 2)    -> "dr"
             */
            ParagraphSplicer.setText(piece.textNode, pieceText.slice(0, offsetInPiece));

            /**
             * EXAMPLE
             *   whole          = "draft"
             *   offsetInPiece  = 2
             *   slice(2)       -> "aft"
             */
            const trailingPieceText = pieceText.slice(offsetInPiece);
            /**
             * WHEN characters remain after the split point
             *   CONSEQUENCE
             *     Create a text node for the tail run.
             *     At the very end, only following sibling nodes move.
             */
            if (trailingPieceText.length > 0) {
                /** In the text node's own spelling: the tail of an `m:t` is another `m:t`. */
                const trailingTextElement = RunSpelling.elementLike(piece.textNode, elementNameWithoutPrefix(piece.textNode));
                ParagraphSplicer.setText(trailingTextElement, trailingPieceText);
                trailingContentParent.appendChild(trailingTextElement);
            }
        }

        let nextSiblingToMove = piece.textNode.nextSibling;
        while (nextSiblingToMove) {
            const siblingAfterMovedNode = nextSiblingToMove.nextSibling;
            trailingContentParent.appendChild(nextSiblingToMove);
            nextSiblingToMove = siblingAfterMovedNode;
        }

        sourceRun.parentNode?.insertBefore(trailingRun, sourceRun.nextSibling);
        return trailingRun;
    }

    /**
     * Moves everything before `piece`'s text node (except `rPr`) into a new run
     * placed before the original, so the original run starts with `piece`.
     * Returns the original run, which now holds `piece` at its head.
     */
    private peelHead(piece: TextPiece): Element {
        const sourceRun = piece.run;
        const leadingRunShell = this.shellOf(sourceRun);
        /** The element whose children hold the piece: the run, or the revision container inside an equation's run. */
        const sourceContentParent = piece.textNode.parentElement as Element;
        const leadingContentParent = this.contentParentFor(piece, leadingRunShell);
        let movedAnyLeadingNode = false;
        let leadingChildNode = sourceContentParent.firstChild;
        while (leadingChildNode && leadingChildNode !== piece.textNode) {
            const nextLeadingSibling = leadingChildNode.nextSibling;
            const leadingChildNodeIsElementNode = leadingChildNode.nodeType === 1;
            const leadingChildNodeIsRunProperties = leadingChildNodeIsElementNode && elementNameWithoutPrefix(leadingChildNode as Element) === "rPr";
            /**
             * WHEN the leading node is rPr
             *   CONSEQUENCE
             *     Leave it on the original run so that run remains the formatting
             *     source for the live piece.
             */
            if (leadingChildNodeIsRunProperties) {
                leadingChildNode = nextLeadingSibling;
                /**
                 * CONSEQUENCE
                 *   Skip moving this rPr node into the peeled head run.
                 */
                continue;
            }
            leadingContentParent.appendChild(leadingChildNode);
            movedAnyLeadingNode = true;
            leadingChildNode = nextLeadingSibling;
        }
        /**
         * WHEN some leading content was moved out
         *   CONSEQUENCE
         *     Insert the peeled head run before the original.
         *     Otherwise no empty sibling is created.
         */
        if (movedAnyLeadingNode) sourceRun.parentNode?.insertBefore(leadingRunShell, sourceRun);
        return sourceRun;
    }

    /** Returns a run containing exactly the piece's characters `fromOffsetInPiece..toOffsetInPiece`. Splits off the tail first, then the head, so the offsets stay valid throughout. */
    /**
     * TYPE
     *   range is a piece-local half-open slice:
     *     [fromOffsetInPiece, toOffsetInPiece)
     *
     * CONSEQUENCE
     *   - fromOffsetInPiece is included
     *   - toOffsetInPiece is the first character after the kept span
     */
    private isolatePieceCharacterRangeInRun(piece: TextPiece, range: { fromOffsetInPiece: number; toOffsetInPiece: number }): Element {
        const keptRangeEndsBeforePieceTextEnds = range.toOffsetInPiece < piece.text.length;
        const pieceHasTrailingSiblingNodes = !!piece.textNode.nextSibling;
        const pieceNeedsTrailingSplit = keptRangeEndsBeforePieceTextEnds || pieceHasTrailingSiblingNodes;
        /**
         * EXAMPLE
         *   piece.text        = "draft"
         *   kept range        = 1..5
         *   splitAfter at 5   -> keeps "raft" in the isolated run
         *   tail text         -> none
         *
         * WHY
         *   Split here even when toOffsetInPiece is at the text end if a next sibling
         *   exists.
         *   -> Nodes after the text element move out of the isolated run.
         */
        if (pieceNeedsTrailingSplit) this.splitAfter(piece, range.toOffsetInPiece);
        /**
         * WHEN the kept slice starts inside the piece
         *   CONSEQUENCE
         *     Split off the leading content so the returned run begins exactly at
         *     fromOffsetInPiece.
         */
        if (range.fromOffsetInPiece > 0) return this.splitAfter(piece, range.fromOffsetInPiece);
        return this.peelHead(piece);
    }

    /**
     * A `w:ins` wrapping a new run of `text` styled like `formattingSourceRun`
     * (a bare run when there is none to copy), with tabs and line breaks written
     * as their glyph elements, since Word ignores those characters inside `w:t`.
     */
    /**
     * TYPE
     *   An insertion-build request (`InsertionOfWords`).
     *
     * CASES
     *   - the words are written as a link -> copy the struck link's run shell, and wrap the run in a
     *                                        new HYPERLINK field inside the insertion
     *   - formattingSourceRun is present  -> copy that run's shell
     *   - formattingSourceRun is null     -> start from a bare w:r because no neighboring run supplies
     *                                        formatting
     *
     * EXAMPLE, "the Annex B" written as a link to https://y.example/fee
     *   <w:ins>[begin] [instrText ' HYPERLINK "https://y.example/fee" '] [separate] <w:r>the Annex B</w:r> [end]</w:ins>
     */
    private insertionOf({ formattingSourceRun: runBesideTheWords, doc, text, writtenAsLink }: InsertionOfWords): Element {
        const formattingSourceRun = ParagraphSplicer.formattingSourceOfInsertion(runBesideTheWords, writtenAsLink);
        /**
         * WHEN the text lands in an equation
         *   CONSEQUENCE
         *     write Word's math spelling instead: one `m:r` holding a `w:ins` that holds the
         *     character formatting and an `m:t`. The run goes into the math argument the
         *     struck text sat in, so an inserted exponent stays an exponent.
         */
        if (formattingSourceRun && RunSpelling.isMath(formattingSourceRun)) return this.mathInsertionOf(formattingSourceRun, text);
        const runWithText = (): Element => {
            const insertedRun = formattingSourceRun ? this.shellOf(formattingSourceRun) : doc.createElementNS(WORDPROCESSINGML_NS, "w:r");
            for (const textFragmentOrSeparator of text.split(/(\t|\r\n|\r|\n)/)) {
                /**
                 * WHEN split() produced an empty fragment
                 *   CONSEQUENCE
                 *     Ignore it so no empty text node is emitted between adjacent
                 *     separators or at the ends.
                 */
                if (textFragmentOrSeparator.length === 0) continue;
                /**
                 * CASES
                 *   - "\r\n" or a lone "\r" -> "\n": each is one line ending, and a "\r" left
                 *                              inside `w:t` is read back by the XML parser as
                 *                              a second line break
                 *   - anything else         -> unchanged
                 */
                const textFragmentOrGlyphChar = textFragmentOrSeparator === "\r\n" || textFragmentOrSeparator === "\r" ? "\n" : textFragmentOrSeparator;
                /**
                 * CASES
                 *   - part is a tab or line break  -> emit its Word glyph element
                 *   - part is ordinary text        -> emit w:t
                 */
                if (isGlyphChar(textFragmentOrGlyphChar)) {
                    const glyphElementNameForTextFragment = GLYPH_ELEMENTS[textFragmentOrGlyphChar];
                    const glyphElementForTextFragment = doc.createElementNS(WORDPROCESSINGML_NS, glyphElementNameForTextFragment);
                    insertedRun.appendChild(glyphElementForTextFragment);
                    /**
                     * CONSEQUENCE
                     *   Skip w:t creation for this fragment: the glyph element is already emitted.
                     */
                    continue;
                }
                const textElement = doc.createElementNS(WORDPROCESSINGML_NS, "w:t");
                ParagraphSplicer.setText(textElement, textFragmentOrGlyphChar);
                insertedRun.appendChild(textElement);
            }
            return insertedRun;
        };

        const insertionWrapper = this.appliedRevision.stamp(doc.createElementNS(WORDPROCESSINGML_NS, "w:ins"));
        /**
         * WHEN the words are plain
         *   CONSEQUENCE
         *     the insertion holds their run alone
         */
        if (writtenAsLink === null) {
            insertionWrapper.appendChild(runWithText());
            return insertionWrapper;
        }
        for (const hyperlinkFieldRun of LinkFieldStriker.hyperlinkFieldRunsAround(runWithText(), writtenAsLink.address)) insertionWrapper.appendChild(hyperlinkFieldRun);
        return insertionWrapper;
    }

    /**
     * The run whose formatting new words take.
     *
     * CASES
     *   - the words are written as a link -> the struck link's own run, so the new link reads as a link
     *                                        (its character style, its colour and underline)
     *   - plain words                      -> the run beside them, as the caller placed them
     *
     * NULL
     *   Plain words with no run beside them (an empty paragraph).
     */
    private static formattingSourceOfInsertion(runBesideTheWords: Element | null, writtenAsLink: ReplacementLinkOfStruckLink | null): Element | null {
        /**
         * CASES
         *   - the new words are written as a link -> the struck link's own run lends its formatting, so the
         *                                            new link looks like the old one
         *   - otherwise                          -> the run beside the words, as for any insertion
         */
        if (writtenAsLink !== null) return writtenAsLink.formattingSourceRun;
        return runBesideTheWords;
    }

    /**
     * An equation's run holding `text` as a tracked insertion, styled like
     * `formattingSourceRun`:
     *
     *   <m:r><m:rPr…/><w:ins …><w:rPr…/><m:t xml:space="preserve">2</m:t></w:ins></m:r>
     *
     * The math properties stay on the run, where the equation renderer reads
     * them, and the character formatting goes inside the insertion with the
     * text, which is where Word writes it. Tabs and line breaks are written as
     * their characters rather than as `w:tab`/`w:br`, which an `m:r` cannot
     * hold: the character count is what the planned offsets are measured in.
     */
    private mathInsertionOf(formattingSourceRun: Element, text: string): Element {
        const insertedRun = RunSpelling.elementLike(formattingSourceRun, "r");
        const insertionWrapper = this.appliedRevision.stamp((formattingSourceRun.ownerDocument as Document).createElementNS(WORDPROCESSINGML_NS, "w:ins"));
        for (const runProperties of RunSpelling.runPropertiesOf(formattingSourceRun)) {
            const clonedRunProperties = runProperties.cloneNode(true) as Element;
            /**
             * CASES, by which properties block this is
             *   - `m:rPr` (the math styling) -> stays on the run, outside the insertion
             *   - `w:rPr` (the character formatting) -> goes inside the insertion, beside the text
             */
            if (RunSpelling.isMath(runProperties)) insertedRun.appendChild(clonedRunProperties);
            else insertionWrapper.appendChild(clonedRunProperties);
        }
        const textElement = RunSpelling.elementLike(formattingSourceRun, "t");
        ParagraphSplicer.setText(textElement, text);
        insertionWrapper.appendChild(textElement);
        insertedRun.appendChild(insertionWrapper);
        return insertedRun;
    }

    /**
     * Moves `beforeNode` and everything after it into a fresh copy of
     * `container`, so something can be placed between the two halves. The copy
     * takes the next revision id: the original keeps its id and stays the
     * revision it was, and the tail becomes a second revision with the same
     * author and date, which is how Word itself records a revision that was
     * split by a later edit.
     */
    private splitContainerBefore(container: Element, beforeNode: Node | null): Element | null {
        if (beforeNode === null) return null;
        const trailingContainerClone = container.cloneNode(false) as Element;
        trailingContainerClone.setAttributeNS(WORDPROCESSINGML_NS, "w:id", this.appliedRevision.takeNextRevisionId());
        let nextNodeToMove: Node | null = beforeNode;
        while (nextNodeToMove) {
            const siblingAfterMovedNode: Node | null = nextNodeToMove.nextSibling;
            trailingContainerClone.appendChild(nextNodeToMove);
            nextNodeToMove = siblingAfterMovedNode;
        }
        return trailingContainerClone;
    }

    /**
     * Inserts `wrapper` before `beforeNode` inside `container`, except when
     * `container` is itself an insertion container: a `w:ins` inside a `w:ins`
     * is invalid, so the container is split and the wrapper goes between the
     * halves, and a container left empty by the split is removed.
     */
    /**
     * TYPE
     *   beforeNode is interpreted as a child of container.
     *
     * CASES
     *   - container accepts wrapper directly
     *       -> insert there
     *   - container is an insertion container
     *       -> split it first so no w:ins nests inside w:ins
     */
    private placeInsertion({ wrapper, container, beforeNode }: { wrapper: Element; container: Node; beforeNode: Node | null }): void {
        const containerElement = container as Element;
        /**
         * CASES
         *   - destination is not an insertion container
         *       -> insert wrapper directly
         *   - destination is an insertion container
         *       -> split the container before inserting
         */
        if (!INSERTION_CONTAINERS.has(elementNameWithoutPrefix(containerElement))) {
            container.insertBefore(wrapper, beforeNode);
            return;
        }
        const containerParentNode = containerElement.parentNode as Node;
        const trailingInsertionContainer = this.splitContainerBefore(containerElement, beforeNode);
        containerParentNode.insertBefore(wrapper, containerElement.nextSibling);
        /**
         * WHEN splitting the insertion container produced a trailing half
         *   CONSEQUENCE
         *     Reattach that tail after the new wrapper.
         */
        if (trailingInsertionContainer) containerParentNode.insertBefore(trailingInsertionContainer, wrapper.nextSibling);
        /**
         * WHEN the original insertion container is empty after the split
         *   CONSEQUENCE
         *     Remove the empty container.
         */
        if (!containerElement.hasChildNodes()) containerParentNode.removeChild(containerElement);
    }
}
